<template>
	<div v-if="isVisible" class="px-4 formField">
		<v-menu
			:close-on-click="false"
			:close-on-content-click="false"
			:open-on-click="false"
			offset-y
			v-model="menuStatus"
		>
			<template v-slot:activator="{ on, attrs }">
				<v-text-field
					@keyup="uppercase"
					:class="getUppercaseClass"
					:disabled="formField.Readonly"
					v-debounce:500ms="onSearchChanged"
					:rules="validationRules"
					:error-messages="validationError"
					clearable
					v-bind="attrs"
					v-on="on"
					:label="formField.Label"
					v-model="selectedValue"
					hide-details="auto"
					@click:clear="onClear"
					@focus="onFocus"
					@blur="onBlur"
				>
					<template
						v-slot:prepend
						v-if="visibleBubble || visibleHelp"
					>
						<div v-if="visibleBubble">
							<v-tooltip
								top
								nudge-top="20"
								:open-on-click="true"
								:open-on-focus="true"
								:open-on-hover="true"
							>
								<template v-slot:activator="{ on, attrs }">
									<v-icon
										color="primary"
										v-bind="attrs"
										v-on="on"
										tabindex="-1"
										>mdi-information-outline</v-icon
									>
								</template>
								{{ bubbleText }}
							</v-tooltip>
						</div>
						<div v-if="visibleHelp">
							<v-tooltip
								top
								nudge-top="20"
								:open-on-click="true"
								:open-on-focus="true"
								:open-on-hover="true"
							>
								<template v-slot:activator="{ on, attrs }">
									<v-icon
										color="primary"
										v-bind="attrs"
										v-on="on"
										tabindex="-1"
										>mdi-help-circle-outline</v-icon
									>
								</template>
								{{ helpText }}
							</v-tooltip>
						</div>
					</template>
					<template v-slot:append>
						<v-icon v-if="menuStatus">mdi-menu-up</v-icon>
						<v-icon v-else class="rotate">mdi-menu-up</v-icon>
					</template>
					<template v-slot:append-outer v-if="canRetainValue">
						<v-icon v-if="!hasRetainedValue" @click="onPinClick"
							>mdi-pin-off-outline</v-icon
						>
						<v-icon
							v-else
							color="primary"
							class="rotate45"
							@click="onPinClick"
							>mdi-pin-outline</v-icon
						>
					</template>
				</v-text-field>
			</template>
			<div v-if="menuStatus" style="background-color: white">
				<v-overlay absolute="absolute" :value="loading" :dark="false">
					<v-card max-width="200px">
						<v-card-text class="d-flex justify-center">
							<v-progress-circular
								:size="25"
								indeterminate
								color="primary"
							/>
							<span class="mx-3 text-button">Loading</span>
						</v-card-text>
					</v-card>
				</v-overlay>
				<v-list
					v-if="gridViewData"
					max-height="320"
					style="overflow-y: scroll"
				>
					<v-list-item-group
						v-model="selected"
						active-class="primary white--text"
						class="field-list"
					>
						<v-list-item
							v-for="(item, index) in gridViewData"
							@mousedown.prevent="onSelectItem(item)"
							:key="index"
							:value="item"
						>
							<v-list-item-content>
								<v-list-item-title>
									{{
										item[gridDataItemKey]
									}}</v-list-item-title
								>
							</v-list-item-content>
						</v-list-item>
					</v-list-item-group>
				</v-list>
				<v-row no-gutters class="ma-2">
					<v-spacer />
					<v-btn
						dense
						small
						color="primary"
						@mousedown.prevent="onShowMore"
						>More</v-btn
					>
				</v-row>
			</div>
		</v-menu>
		<LinkFieldModal
			v-if="currentGridView && showDialog"
			@close="closeDialog()"
			@rowClicked="onRowClicked"
			:dialog="showDialog"
			:gridView="currentGridView"
			:action="loadAction"
			:inventoryFormId="inventoryForm.Id"
		/>
	</div>
</template>
<script>
import { gridViewTypes } from '../../../../enums/gridViewTypes';
import { mapGetters } from 'vuex';
import {
	InventoryFormGetters,
	InventoryFormFormGetters,
	InventoryFormRetainedValuesGetters,
	AuthGetterTypes,
} from '../../../../store/getters';
import {
	InventoryFormFormActions,
	InventoryFormRetainedValuesActions,
} from '../../../../store/actions';
import formFieldMixin from '../../../../mixins/inventoryForm/formFieldMixin';
import dataFieldUtils from '../../../../utils/dataFieldUtils';
import gridViewFieldsUtils from '../../../../utils/gridViewFieldsUtils';
import LinkFieldModal from '../LinkFieldModal.vue';

export default {
	name: 'LinkField',
	mixins: [formFieldMixin],
	components: { LinkFieldModal },
	computed: {
		...mapGetters({
			inventoryForm: [InventoryFormFormGetters.SELECTED_INVENTORY_FORM],
			defaultGridViews: [InventoryFormGetters.GET_GRID_VIEWS],
			gridViews: [InventoryFormFormGetters.GRID_VIEWS],
			retainedValues: [
				InventoryFormRetainedValuesGetters.RETAINED_VALUES,
			],
			currentUserId: [AuthGetterTypes.CURRENT_USER_ID],
		}),

		gridViewField() {
			if (!this.formField) return null;
			return this.formField.GridViewFields[0];
		},
		dataField() {
			if (!this.gridViewField) return null;
			return this.gridViewField.DataField;
		},
		currentGridViewEntityId() {
			if (!this.dataField) return null;
			return this.dataField.EntityId;
		},
		currentGridView() {
			if (!this.currentGridViewEntityId) return null;
			let entityId = this.currentGridViewEntityId;
			let gv = this.gridViews.find(
				(x) =>
					x.Type === gridViewTypes.InventoryFormFilters &&
					x.EntityId == entityId
			);
			if (gv) {
				return gv;
			} else {
				gv = this.defaultGridViews.find(
					(x) =>
						x.Type === gridViewTypes.Grids &&
						x.EntityId == entityId &&
						x.UserId == null
				);
				return gv;
			}
		},
		column() {
			if (!this.currentGridView) return [];
			let visibleColumn = this.currentGridView.Columns.find(
				(x) => x.DataFieldId === this.gridViewField.DataFieldId
			);
			return visibleColumn;
		},
		gridDataItemKey() {
			if (!this.column) return '';
			return this.column.FieldName;
		},
		relatedFields() {
			if (!this.currentGridView) return [];
			if (!this.inventoryForm) return [];
			let result = [];

			let formFields = this.inventoryForm.FormFields.filter(
				(x) =>
					x.GridViewFields &&
					x.GridViewFields.length == 1 &&
					x.GridViewFields[0].LinkFieldId ==
						this.gridViewFields[0].LinkFieldId
			);
			formFields.forEach((f) => {
				let corespondingColumn = this.currentGridView.Columns.find(
					(c) => c.DataFieldId == f.GridViewFields[0].DataFieldId
				);
				let relatedItem = {
					formField: f,
					gridViewField: f.GridViewFields[0],
					recordKey: gridViewFieldsUtils.getFieldName(
						f.GridViewFields[0]
					),
					curentGridViewCorrespondingGridViewField:
						corespondingColumn,
					currentGridViewKey:
						gridViewFieldsUtils.getFieldName(corespondingColumn),
				};
				result.push(relatedItem);
			});
			return result;
		},
		currentGridViewColumn() {
			if (!this.relatedFields) return null;
			var column = this.relatedFields.find(
				(x) => x.formField.Id == this.formField.Id
			);
			if (!column) return null;
			return column.curentGridViewCorrespondingGridViewField;
		},
		currentGridViewColumnsId() {
			if (!this.currentGridViewColumn) return [];
			return this.currentGridViewColumn.Id;
		},

		recordSelected() {
			if (!this.relatedFields) return null;
			if (!this.recordSelectedValue) return null;
			let selectedItem = {};
			this.relatedFields.forEach((x) => {
				selectedItem[x.currentGridViewKey] = this.record[x.recordKey];
			});
			let idKey = dataFieldUtils.getDataFieldName(
				this.gridViewField.LinkField
			);
			selectedItem.Id = this.record[idKey];
			return selectedItem;
		},
	},
	data() {
		return {
			loadAction: InventoryFormFormActions.LOAD_GRID_VIEW_DATA,
			hasFocus: false,
			showDialog: false,
			menuStatus: false,
			selected: null,
			gridViewData: [],
			loading: false,
			advancedFilter: { Operator: 1, Filters: [] },
		};
	},
	mounted() {
		this.defaultRules.push((v) => {
			if (this.formField.Required && !this.isBulkEdit) {
				if (!v) return false || 'The value is required';
				else return true;
			} else return true;
		});
		if (this.recordSelectedValue) {
			this.selectedValue = this.recordSelectedValue;
			this.selected = this.recordSelected;
		}
	},
	methods: {
		onFocus() {
			this.hasFocus = true;
			this.menuStatus = true;
			this.onSearchChanged();
		},

		onBlur() {
			this.hasFocus = false;
			this.menuStatus = false;
			if (
				this.formField.ForceChoice &&
				this.selectedValue &&
				this.selectedValue != this.recordSelectedValue &&
				!this.selected
			) {
				let payload = this.buildPayload();
				this.$store
					.dispatch(
						InventoryFormFormActions.LOAD_GRID_VIEW_DATA,
						payload
					)
					.then((resp) => {
						const matchingItem = resp.Data.find(
							(item) =>
								(item[this.gridDataItemKey] || '')
									.trim()
									.toLowerCase() ===
								(this.selectedValue || '').trim().toLowerCase()
						);
						if (matchingItem) {
							this.selected = matchingItem;
							this.selectedValue =
								matchingItem[this.gridDataItemKey];
						} else {
							this.selectedValue = null;
							this.selected = null;
						}

						this.updateRecord();
					});
			} else if (
				this.selectedValue != this.recordSelectedValue &&
				!this.selected
			) {
				this.updateRecord();
			}
		},

		onRecordChanged() {
			if (
				this.recordSelectedValue &&
				this.selectedValue != this.recordSelectedValue
			) {
				this.selectedValue = this.recordSelectedValue;
				this.selected = this.recordSelected;
				return;
			}

			if (
				this.selectedValue &&
				this.selectedValue != this.recordSelectedValue
			) {
				this.selectedValue = null;
				this.selected = null;
			}
		},

		onHideFormField() {
			this.selectedValue = null;
			this.selected = null;
			this.onSelectItem(null);
		},

		closeDialog() {
			this.showDialog = false;
			this.menuStatus = false;
		},

		updateRecord() {
			let keys = [];
			keys.push({ key: this.recordKeys[0], value: this.selectedValue });
			let idKey = dataFieldUtils.getDataFieldName(
				this.gridViewField.LinkField
			);
			if (this.selected) {
				this.relatedFields.forEach((f) => {
					if (f.recordKey != this.recordKeys[0]) {
						//the list only returns the columns it was asked for; a related field the selected row
						//carries no column for keeps the value it already has instead of being cleared
						if (!(f.currentGridViewKey in this.selected)) return;
						let itemValue = null;
						if (this.selected[f.currentGridViewKey]) {
							itemValue = this.selected[f.currentGridViewKey];
						}
						let item = { key: f.recordKey, value: itemValue };
						keys.push(item);
					}
				});
				if (this.selected.Id && this.selected.Id > 0) {
					keys.push({ key: idKey, value: this.selected.Id });
				} else {
					keys.push({ key: idKey, value: this.getIdKeyValue() });
				}
			} else {
				keys.push({ key: idKey, value: this.getIdKeyValue() });
			}

			this.skipNextRecordUpdate = true;
			this.$emit('selectedValueChanged', keys);
			this.menuStatus = false;
		},

		onSelectedValueChanged() {
			//does Nothing -> function is called from mixin; For this component we handle selected not selectedValue;selectedValue=search criteria;
			this.selected = null;
			this.gridViewData = [];
		},
		onSetInteraction(setValue) {
			this.selectedValue = setValue;
			this.selected = null;
			this.menuStatus = false;
			this.updateRecord();
		},
		onClearInteraction() {
			this.selectedValue = null;
			this.selected = null;
			this.menuStatus = false;
			this.updateRecord();
		},
		onRowClicked(selectedRow) {
			this.onSelectItem(selectedRow);

			if (this.relatedFields.length > 0)
				this.relatedFields.forEach((x) => {
					let retainedValue = this.retainedValues.find(
						(y) =>
							y.formFieldId == x.formField.Id &&
							this.currentUserId == y.userId &&
							x.formField.InventoryFormId == y.inventoryFormId
					);
					if (retainedValue) {
						let retainedValueKey = gridViewFieldsUtils.getFieldName(
							x.gridViewField
						);
						let rowKey = dataFieldUtils.getDataFieldName(
							x.gridViewField.DataField
						);
						if (retainedValueKey && rowKey) {
							let value = [
								{
									key: retainedValueKey,
									value: selectedRow[rowKey] || '',
								},
							];
							this.$store.dispatch(
								InventoryFormRetainedValuesActions.UPDATE,
								{
									userId: this.currentUserId,
									inventoryFormId:
										retainedValue.inventoryFormId,
									formFieldId: retainedValue.formFieldId,
									value: value,
								}
							);
						}
					}
				});
			this.closeDialog();
		},
		onClear() {
			this.selectedValue = null;
			this.selected = null;
			this.gridViewData = [];
			this.onSelectItem(null);
			this.menuStatus = true;
			this.onSearchChanged();
		},

		onSelectItem(selectedRow) {
			this.selected = selectedRow;
			if (selectedRow) {
				this.selectedValue = selectedRow[this.gridDataItemKey];
			} else {
				this.selectedValue = null;
				this.selected = null;
			}

			this.menuStatus = false;
			this.updateRecord();
		},

		onShowMore() {
			this.showDialog = true;
			this.menuStatus = false;
		},
		onSearchChanged() {
			if (!this.hasFocus) return;
			this.loading = true;
			this.selected = null;
			this.menuStatus = true;
			let payload = this.buildPayload();
			this.$store
				.dispatch(InventoryFormFormActions.LOAD_GRID_VIEW_DATA, payload)
				.then((resp) => {
					this.loading = false;
					if (resp) {
						this.gridViewData = resp.Data;
					} else {
						this.gridViewData = [];
					}
				});
		},

		buildAdvancedFilter() {
			if (!this.relatedFields) return null;
			if (this.relatedFields.length == 0) return null;
			let result = { Operator: 1, Filters: [] };
			this.relatedFields.forEach((f) => {
				if (
					this.recordKeys[0] != f.recordKey &&
					this.record[f.recordKey]
				) {
					let filter = {
						Value: this.record[f.recordKey],
						Operator: 1,
						FieldId: f.curentGridViewCorrespondingGridViewField.Id,
					};
					result.Filters.push(filter);
				}
			});
			if (result.Filters.length == 0) return null;
			return result;
		},
		getIdKeyValue() {
			let result = null;
			this.relatedFields.forEach((f) => {
				if (
					this.recordKeys[0] != f.recordKey &&
					this.record[f.recordKey]
				) {
					result = null;
				}
			});
			return result;
		},
		buildPayload() {
			let payload = {
				ViewId: this.currentGridView.Id,
				ExcludeId: true,
				Page: 1,
				RowsPerPage: 10,
				Columns: [this.currentGridViewColumnsId],
				IncludeCount: true,
				InventoryFormId: this.inventoryForm.Id,
			};
			if (this.selectedValue) {
				payload.QuickFilter = {
					FieldId: this.currentGridViewColumnsId,
					Value: this.selectedValue,
				};
			}
			payload.AdvancedFilter = this.buildAdvancedFilter();
			return payload;
		},
	},
};
</script>
<style lang="scss">
.v-menu__content {
	background-color: white;
}
</style>
